import { Table } from "@tanstack/react-table";
import { useEffect } from "react";
import useTableColumnSizing from "./useTableColumnSizing";
import useTableColumnVisibility from "./useTableColumnVisibility";
import { DEFAULT_SELECT_COLUMN_SIZE } from "@/constants";
import useTableColumnPinning from "./useTableColumnPinning";

/**
 * Sets CSS variables on the table's parent element for column sizes and sticky positions. Used by
 * DataTableHead and DataTableCell to render columns with correct widths and left offsets for pinned
 * (sticky) columns when horizontally scrolling.
 */
const useTableColumnSizeAndPositionStyle = <TData,>(
  table: Table<TData>,
  parentRef: React.ForwardedRef<HTMLDivElement>,
) => {
  const { columnSizing } = useTableColumnSizing();
  const { columnVisibility } = useTableColumnVisibility();
  const { columnPinning } = useTableColumnPinning();

  useEffect(() => {
    const leftPinnedHeaders = table.getLeftLeafHeaders();

    table.getLeafHeaders().forEach((header) => {
      const columnId = header.column.id;
      if (!columnVisibility[columnId]) return;

      if (parentRef && "current" in parentRef) {
        // Size: used for width in grid layout
        const size = header.getSize();
        parentRef.current?.style.setProperty(
          `--header-${columnId}-size`,
          size.toString(),
        );
        parentRef.current?.style.setProperty(
          `--col-${columnId}-size`,
          size.toString(),
        );
        // Position: sum of widths of pinned columns to the left of this one.
        // Required for sticky columns so they don't overlap when scrolling.
        const pinnedIndex = header.column.getPinnedIndex();
        const leftPosition = leftPinnedHeaders
          .filter(
            (c) =>
              c.column.getIsPinned() && c.column.getPinnedIndex() < pinnedIndex,
          )
          .reduce((acc, c) => acc + c.getSize(), 0);
        parentRef.current?.style.setProperty(
          `--col-${columnId}-position`,
          leftPosition.toString(),
        );
      }
    });

    if (parentRef && "current" in parentRef) {
      parentRef.current?.style.setProperty(
        `--table-width`,
        table.getTotalSize().toString(),
      );
      // Select column size: use computed size when present, fallback otherwise.
      const selectHeader = table
        .getLeafHeaders()
        .find((h) => h.column.id === "select");
      const selectSize = selectHeader?.getSize() ?? DEFAULT_SELECT_COLUMN_SIZE;
      parentRef.current?.style.setProperty(
        `--header-select-size`,
        selectSize.toString(),
      );
      parentRef.current?.style.setProperty(
        `--col-select-size`,
        selectSize.toString(),
      );
    }

    // We want to update the css vars when sizing changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [table, columnSizing, columnVisibility, columnPinning, parentRef]);
};

export default useTableColumnSizeAndPositionStyle;
